// src/components/ui/RoleBasedSidebar.jsx
import { useMemo } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getRoleKey } from "../../utils/authHelpers";
import Icon from "../AppIcon";

const RoleBasedSidebar = ({ isCollapsed = false, onToggle, userRole }) => {
  const { user } = useAuth();
  const location = useLocation();

  if (!user) {
    return null; // Don't render sidebar if user is not authenticated
  }

  // Use the passed userRole prop if available, otherwise fall back to user context
  const userRoleKey = useMemo(() => {
    if (userRole) {
      return getRoleKey(userRole);
    }
    return getRoleKey(user?.role);
  }, [userRole, user?.role]);

  // Memoize navigation items to prevent unnecessary recalculations
  const navigationItems = useMemo(() => {
    const getNavigationItems = (roleKey) => {
      const navigationMap = {
        admin: [
          {
            label: "Master Admin Dashboard",
            path: "/master-admin-dashboard",
            icon: "LayoutDashboard",
            tooltip: "Master admin overview and system metrics",
          },
          {
            label: "User Management",
            path: "/user-management",
            icon: "Users",
            tooltip: "Manage platform users and permissions",
          },
          {
            label: "Recruiter Management",
            path: "/recruiter-management",
            icon: "Building",
            tooltip: "Manage recruitment agencies and profiles",
          },
          {
            type: "section",
            label: "RECRUITER",
          },
          {
            label: "Draft Biodata",
            path: "/clab-biodata-drafts",
            icon: "FileText",
            tooltip: "Monitor recruiter drafts before submission",
          },
          {
            label: "Pending Reviews",
            path: "/clab-biodata-pending",
            icon: "Clock",
            tooltip: "Review biodata submissions awaiting approval",
          },
          {
            label: "Amended Biodata",
            path: "/clab-biodata-amended",
            icon: "Edit",
            tooltip: "Review biodata that has been amended after rejection",
          },
          {
            label: "Approved Biodata",
            path: "/clab-biodata-approved",
            icon: "CheckCircle",
            tooltip: "View approved biodata available to employers",
          },
          {
            label: "Rejected Biodata",
            path: "/clab-biodata-rejected",
            icon: "XCircle",
            tooltip: "View rejected biodata with rejection reasons",
          },
          {
            type: "section",
            label: "EMPLOYER",
          },
          {
            label: "Shortlisted Workers",
            path: "/clab-biodata-shortlisted",
            icon: "UserCheck",
            tooltip: "Monitor biodata currently on hold by employers",
          },
          {
            label: "Unselected Workers",
            path: "/clab-unselected-workers",
            icon: "UserX",
            tooltip: "View approved biodata not yet selected by employers",
          },
          {
            label: "Selection Requests",
            path: "/clab-selection-requests",
            icon: "FileCheck",
            tooltip: "Review employer worker selection requests",
          },
          {
            label: "Arrival Notifications",
            path: "/clab-arrival-notifications",
            icon: "Mail",
            tooltip: "Manage worker arrival notifications to employers",
          },
          {
            label: "Special Quota Review",
            path: "/admin-special-quotas",
            icon: "ClipboardCheck",
            tooltip: "Approve special quota submissions and receipts",
          },
          {
            type: "section",
            label: "REPORT",
          },
          {
            label: "Reports & Analytics",
            path: "/clab-reports-analytics",
            icon: "BarChart3",
            tooltip: "View comprehensive system reports",
          },
        ],
        clab: [
          {
            label: "CLAB Dashboard",
            path: "/clab-dashboard",
            icon: "LayoutDashboard",
            tooltip: "CLAB overview and biodata review metrics",
          },
          {
            type: "section",
            label: "RECRUITER",
          },
          {
            label: "Draft Biodata",
            path: "/clab-biodata-drafts",
            icon: "FileText",
            tooltip: "Monitor recruiter drafts before submission",
          },
          {
            label: "Pending Reviews",
            path: "/clab-biodata-pending",
            icon: "Clock",
            tooltip: "Review biodata submissions awaiting approval",
          },
          {
            label: "Amended Biodata",
            path: "/clab-biodata-amended",
            icon: "Edit",
            tooltip: "Review biodata that has been amended after rejection",
          },
          {
            label: "Approved Biodata",
            path: "/clab-biodata-approved",
            icon: "CheckCircle",
            tooltip: "View approved biodata available to employers",
          },
          {
            label: "Rejected Biodata",
            path: "/clab-biodata-rejected",
            icon: "XCircle",
            tooltip: "View rejected biodata with rejection reasons",
          },
          {
            type: "section",
            label: "EMPLOYER",
          },
          {
            label: "Shortlisted Workers",
            path: "/clab-biodata-shortlisted",
            icon: "UserCheck",
            tooltip: "Monitor biodata currently on hold by employers",
          },
          {
            label: "Unselected Workers",
            path: "/clab-unselected-workers",
            icon: "UserX",
            tooltip: "View approved biodata not yet selected by employers",
          },
          {
            label: "Selection Requests",
            path: "/clab-selection-requests",
            icon: "FileCheck",
            tooltip: "Review employer worker selection requests",
          },
          {
            label: "Arrival Notifications",
            path: "/clab-arrival-notifications",
            icon: "Mail",
            tooltip: "Manage worker arrival notifications to employers",
          },
          {
            label: "Special Quota Review",
            path: "/admin-special-quotas",
            icon: "ClipboardCheck",
            tooltip: "Approve special quota submissions and receipts",
          },
          {
            type: "section",
            label: "REPORT",
          },
          {
            label: "Reports & Analytics",
            path: "/clab-reports-analytics",
            icon: "BarChart3",
            tooltip: "View biodata statistics and system reports",
          },
        ],
        recruiter: [
          {
            label: "Dashboard",
            path: "/recruiter-dashboard",
            icon: "LayoutDashboard",
            tooltip: "Recruiter dashboard and metrics",
          },
          {
            label: "Biodata Management",
            path: "/recruiter-biodata-management",
            icon: "FileText",
            tooltip: "Create and manage worker biodata entries",
          },
        ],
        employer: [
          {
            label: "Dashboard",
            path: "/employer-dashboard",
            icon: "LayoutDashboard",
            tooltip: "Employer overview and highlights",
          },
          {
            label: "Application Management",
            path: "/employer-application-management",
            icon: "ClipboardList",
            tooltip: "Track E-CLAB applications, quotas, and progress",
          },
          {
            label: "Special Quota",
            path: "/employer-special-quota/new",
            icon: "ClipboardPlus",
            tooltip: "Create a new eQuotaSpecial request",
          },
          {
            label: "Submitted Applications",
            path: "/employer-submitted-applications",
            icon: "FileCheck",
            tooltip: "View submitted applications and workers",
          },
          {
            label: "Selected Workers",
            path: "/employer-approved-workers",
            icon: "Users",
            tooltip: "Monitor CLAB decisions and manage rejected selections",
          },
          {
            label: "Shortlisted Workers",
            path: "/employer-shortlist",
            icon: "Heart",
            tooltip: "View and manage your shortlisted workers",
          },
        ],
      };

      return navigationMap[roleKey] || navigationMap.clab; // Default to CLAB role
    };

    return getNavigationItems(userRoleKey);
  }, [userRoleKey]);

  const isActivePath = (path) => {
    return location.pathname === path;
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <aside
        className={`fixed left-0 top-16 h-[calc(100vh-4rem)] bg-surface border-r border-secondary-200 z-999 transition-all duration-300 ease-smooth ${
          isCollapsed ? "w-16" : "w-60"
        } hidden lg:block`}
      >
        <nav className="h-full flex flex-col">
          {/* Navigation Items */}
          <div className="flex-1 px-3 py-6 space-y-2 overflow-y-auto scrollbar-thin">
            {navigationItems.map((item, index) => {
              // Render section header
              if (item.type === "section") {
                return !isCollapsed ? (
                  <div key={`section-${index}`} className="pt-4 pb-2 px-3">
                    <h3 className="text-xs font-semibold text-secondary-500 uppercase tracking-wider">
                      {item.label}
                    </h3>
                  </div>
                ) : (
                  <div key={`section-${index}`} className="pt-2 pb-1">
                    <div className="h-px bg-secondary-300 mx-2"></div>
                  </div>
                );
              }

              // Render navigation link
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `nav-link group relative ${
                      isActive || isActivePath(item.path)
                        ? "nav-link-active bg-primary-50 text-primary-700 border-r-2 border-primary"
                        : "nav-link-inactive text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900"
                    }`
                  }
                  title={isCollapsed ? item.tooltip : ""}
                >
                  <Icon
                    name={item.icon}
                    size={20}
                    className={`${
                      isCollapsed ? "mx-auto" : "mr-3"
                    } transition-colors duration-150`}
                  />
                  {!isCollapsed && (
                    <span className="font-medium">{item.label}</span>
                  )}

                  {/* Tooltip for collapsed state */}
                  {isCollapsed && (
                    <div className="absolute left-full ml-2 px-2 py-1 bg-secondary-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-1001 pointer-events-none">
                      {item.label}
                    </div>
                  )}
                </NavLink>
              );
            })}
          </div>

          {/* Collapse Toggle Button */}
          <div className="p-3 border-t border-secondary-200">
            <button
              onClick={onToggle}
              className="w-full flex items-center justify-center p-2 rounded-md text-secondary-600 hover:bg-secondary-100 hover:text-secondary-900 transition-colors duration-150"
              title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              <Icon
                name={isCollapsed ? "ChevronRight" : "ChevronLeft"}
                size={20}
              />
            </button>
          </div>
        </nav>
      </aside>

      {/* Mobile Sidebar Overlay */}
      <div
        className={`lg:hidden fixed inset-0 z-999 transition-opacity duration-300 ${
          isCollapsed ? "opacity-0 pointer-events-none" : "opacity-100"
        }`}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-secondary-900 bg-opacity-50"
          onClick={onToggle}
        />

        {/* Sidebar */}
        <aside
          className={`absolute left-0 top-16 h-[calc(100vh-4rem)] w-60 bg-surface border-r border-secondary-200 transform transition-transform duration-300 ease-smooth ${
            isCollapsed ? "-translate-x-full" : "translate-x-0"
          }`}
        >
          <nav className="h-full flex flex-col">
            {/* Navigation Items */}
            <div className="flex-1 px-3 py-6 space-y-2 overflow-y-auto scrollbar-thin">
              {navigationItems.map((item, index) => {
                // Render section header
                if (item.type === "section") {
                  return (
                    <div key={`section-${index}`} className="pt-4 pb-2 px-3">
                      <h3 className="text-xs font-semibold text-secondary-500 uppercase tracking-wider">
                        {item.label}
                      </h3>
                    </div>
                  );
                }

                // Render navigation link
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={onToggle}
                    className={({ isActive }) =>
                      `nav-link ${
                        isActive || isActivePath(item.path)
                          ? "nav-link-active bg-primary-50 text-primary-700 border-r-2 border-primary"
                          : "nav-link-inactive text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900"
                      }`
                    }
                  >
                    <Icon name={item.icon} size={20} className="mr-3" />
                    <span className="font-medium">{item.label}</span>
                  </NavLink>
                );
              })}
            </div>

            {/* Close Button */}
            <div className="p-3 border-t border-secondary-200">
              <button
                onClick={onToggle}
                className="w-full flex items-center justify-center p-2 rounded-md text-secondary-600 hover:bg-secondary-100 hover:text-secondary-900 transition-colors duration-150"
              >
                <Icon name="X" size={20} className="mr-2" />
                <span>Close</span>
              </button>
            </div>
          </nav>
        </aside>
      </div>
    </>
  );
};

export default RoleBasedSidebar;
